import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  UserSession,
  LoginResult,
  RegisterResult,
  AuthCredentials,
  AuthToken,
} from '@interfaces/index'

// Raw API response shape from /api/auth/login and /api/auth/register
interface AuthApiResponse {
  userId: string
  email: string | null
  displayName: string
  playerCode: string
  emailVerified: boolean
  tokens: AuthToken
}

function mapApiResponse(raw: AuthApiResponse): UserSession {
  return {
    userId: raw.userId,
    email: raw.email,
    displayName: raw.displayName,
    playerCode: raw.playerCode,
    emailVerified: raw.emailVerified,
    token: raw.tokens,
  }
}

export interface ProfileUpdate {
  displayName?: string
  email?: string
  newPassword?: string
  currentPassword?: string
}

export interface UpdateProfileResult {
  success: boolean
  error?: string
}

interface AuthState {
  session: UserSession | null
  isLoggingIn: boolean
  authError: string | null
  // actions
  login: (credentials: AuthCredentials) => Promise<LoginResult>
  register: (
    credentials: AuthCredentials & { displayName: string }
  ) => Promise<RegisterResult>
  logout: () => void
  refreshSession: () => Promise<boolean>
  clearError: () => void
  updateProfile: (data: ProfileUpdate) => Promise<UpdateProfileResult>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      isLoggingIn: false,
      authError: null,

      login: async (credentials) => {
        set({ isLoggingIn: true, authError: null })
        try {
          const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
          })
          if (!res.ok) {
            const err = (await res.json()) as { error: string }
            set({ isLoggingIn: false, authError: err.error })
            return { success: false, error: 'INVALID_CREDENTIALS' as const }
          }
          const raw = (await res.json()) as AuthApiResponse
          const session = mapApiResponse(raw)
          set({ session, isLoggingIn: false })
          // Load server-side progression (awaited so the store is ready before navigation)
          const { useProgressionStore } = await import('./useProgressionStore.js')
          await useProgressionStore.getState().syncFromServer()
          return { success: true, session }
        } catch {
          set({ isLoggingIn: false, authError: 'NETWORK_ERROR' })
          return { success: false, error: 'NETWORK_ERROR' as const }
        }
      },

      register: async (creds) => {
        set({ isLoggingIn: true, authError: null })
        try {
          const res = await fetch('/api/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(creds),
          })
          if (!res.ok) {
            const err = (await res.json()) as { error: string }
            set({ isLoggingIn: false, authError: err.error })
            return { success: false, error: 'EMAIL_TAKEN' as const }
          }
          const raw = (await res.json()) as AuthApiResponse
          const session = mapApiResponse(raw)
          set({ session, isLoggingIn: false })
          // Sync freshly created player record from server
          const { useProgressionStore } = await import('./useProgressionStore.js')
          await useProgressionStore.getState().syncFromServer()
          return { success: true, session }
        } catch {
          set({ isLoggingIn: false, authError: 'NETWORK_ERROR' })
          return { success: false, error: 'NETWORK_ERROR' as const }
        }
      },

      logout: () => {
        // Fire-and-forget save before clearing the session
        import('./useProgressionStore.js').then(m => {
          void m.useProgressionStore.getState().saveToServer()
        })
        set({ session: null })
      },

      refreshSession: async () => {
        const { session } = get()
        if (session === null) return false
        try {
          const res = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: session.token.refreshToken }),
          })
          if (!res.ok) {
            set({ session: null })
            return false
          }
          const token = (await res.json()) as AuthToken
          set({ session: { ...session, token } })
          return true
        } catch {
          return false
        }
      },

      clearError: () => set({ authError: null }),

      updateProfile: async (data) => {
        const { session, refreshSession } = get()
        if (session === null) return { success: false, error: 'NOT_LOGGED_IN' }

        const doRequest = async (accessToken: string) =>
          fetch('/api/user/profile', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(data),
          })

        try {
          let res = await doRequest(session.token.accessToken)

          // If the token expired, refresh once and retry
          if (res.status === 401) {
            const refreshed = await refreshSession()
            if (!refreshed) return { success: false, error: 'SESSION_EXPIRED' }
            const newToken = get().session?.token.accessToken ?? ''
            res = await doRequest(newToken)
          }

          if (!res.ok) {
            const body = (await res.json()) as { error?: string }
            return { success: false, error: body.error ?? 'UNKNOWN_ERROR' }
          }

          const updated = (await res.json()) as { displayName: string; email: string }
          // Patch the session in-place so other components see updated name/email
          set((s) =>
            s.session !== null
              ? { session: { ...s.session, displayName: updated.displayName, email: updated.email } }
              : {}
          )
          return { success: true }
        } catch {
          return { success: false, error: 'NETWORK_ERROR' }
        }
      },
    }),
    { name: 'ultimaze-auth' }
  )
)
